/* eslint-disable eslint-comments/disable-enable-pair -- columns extracted from carrier card */
/* eslint-disable sonarjs/cognitive-complexity, sonarjs/no-selector-parameter -- columns extracted from carrier card */
import { SvgIcon } from "@tql/ui-components-react/kendo/common";
import { Tooltip } from "@tql/ui-components-react/kendo/tooltip";
import { DatePicker } from "@tql/ui-components-react/tql/dateinputs";
import { DropDownList } from "@tql/ui-components-react/tql/dropdowns";
import {
	NumericTextBox,
	TextBox,
} from "@tql/ui-components-react/tql/inputs/beta";
import {
	helpCircleLine,
	shieldOff,
} from "@tql/ui-components-react/untitled/icons";
import type { ReactNode } from "react";

import type { CarrierSelectionPageLoadData } from "~/features/quote/hooks/use-carrier-selection-hooks.ts";
import type { CarrierPrice } from "~/shared/schemas/carrier-schema.ts";
import { formatCurrency } from "~/shared/utils/string-fns.ts";

import type { CarrierCardColumnConfig } from "../../shared/carrier-card-layout.tsx";

const formatExpirationDate = (dateString: string): string => {
	if (!dateString) {
		return "EXP Date: N/A";
	}

	const date = new Date(dateString);

	if (Number.isNaN(date.getTime())) {
		return "EXP Date: Invalid";
	}

	const month = String(date.getMonth() + 1).padStart(2, "0");
	const day = String(date.getDate()).padStart(2, "0");
	const year = date.getFullYear();
	return `EXP Date: ${month}/${day}/${year}`;
};

const formatExpirationDateValue = (dateString: string): string => {
	if (!dateString) {
		return "N/A";
	}

	const date = new Date(dateString);

	if (Number.isNaN(date.getTime())) {
		return "Invalid";
	}

	const month = String(date.getMonth() + 1).padStart(2, "0");
	const day = String(date.getDate()).padStart(2, "0");
	const year = date.getFullYear();
	return `${month}/${day}/${year}`;
};

type UseCarrierCardColumnsParams = {
	carrierPrice: CarrierPrice;
	carrierPriceDisplayName: string;
	isInternal: boolean;
	isAdHocCarrier: boolean;
	isDisabled: boolean;
	isSimulated: boolean;
	isReadOnly?: boolean;
	isReratePrevCarrier?: boolean;
	showLiabilityWarning?: boolean;
	requiredIndicator: ReactNode;
	localCarrierQuoteId: string;
	pageData: CarrierSelectionPageLoadData | undefined;
	handleBlur: () => void;
	handleCarrierRateChange: (newCarrierRate: number | null) => void;
	setLocalCarrierQuoteId: (value: string) => void;
	updateAdHocCarrier: (
		id: number,
		field: string,
		value: number | string | { id: number; name: string },
	) => void;
	updateCarrierPriceMargin: (params: {
		carrierId: number;
		marginValue: number;
	}) => void;
};

export function useCarrierCardColumns({
	carrierPrice,
	carrierPriceDisplayName,
	isInternal,
	isAdHocCarrier,
	isDisabled,
	isSimulated,
	isReadOnly = false,
	isReratePrevCarrier = false,
	showLiabilityWarning = false,
	requiredIndicator,
	localCarrierQuoteId,
	pageData,
	handleBlur,
	handleCarrierRateChange,
	setLocalCarrierQuoteId,
	updateAdHocCarrier,
	updateCarrierPriceMargin,
}: UseCarrierCardColumnsParams): CarrierCardColumnConfig[] {
	// When the card is read-only, force the non-editable rendering branches
	// regardless of whether the carrier is ad-hoc.
	const showInputs = isAdHocCarrier && !isReadOnly;
	const formattedCustomerRate = formatCurrency(carrierPrice.customerRate);
	// Shrinks the mobile rate font so very high dollar amounts don't overflow the header row
	const isLongCustomerRate = formattedCustomerRate.length >= 10;
	return [
		{
			id: "customer-rate",
			title: "Customer Rate",
			className: isLongCustomerRate
				? "customer-rate-col customer-rate-col-long"
				: "customer-rate-col",
			content: <strong>{formattedCustomerRate}</strong>,
		},
		{
			id: "exp-date",
			shouldShow: !isInternal,
			group: "details",
			title: "Exp Date",
			className: "exp-date-col",
			content: formatExpirationDateValue(
				carrierPrice.carrierQuoteExpirationDate,
			),
		},
		{
			id: "carrier-rate",
			shouldShow: isInternal,
			group: "details",
			title: (
				<>
					Carrier Rate
					{requiredIndicator}
				</>
			),
			className: "carrier-rate-col",
			content: showInputs ? (
				<NumericTextBox
					disabled={isDisabled}
					format={"c2"}
					onBlur={handleBlur}
					onChange={(e) => {
						handleCarrierRateChange(e.value ?? null);
					}}
					placeholder={"$0.00"}
					spinners={false}
					value={carrierPrice.carrierRate ?? null}
				/>
			) : (
				<>
					{formatCurrency(carrierPrice.carrierRate)}
					<div className={"carrier-card-expiration-date"}>
						{formatExpirationDate(carrierPrice.carrierQuoteExpirationDate)}
					</div>
				</>
			),
		},
		{
			id: "margin",
			shouldShow: isInternal,
			group: "details",
			title: (
				<>
					Margin
					{requiredIndicator}
				</>
			),
			className: "carrier-rate-col",
			content:
				isReadOnly || isReratePrevCarrier ? (
					<div>{formatCurrency(carrierPrice.brokerMargin ?? 0)}</div>
				) : (
					<div>
						<NumericTextBox
							disabled={Boolean(isDisabled && isAdHocCarrier) || isSimulated}
							format={"c2"}
							onBlur={handleBlur}
							onChange={(event) => {
								if (isAdHocCarrier) {
									updateAdHocCarrier(
										carrierPrice.id,
										"brokerMargin",
										event.value ?? 0,
									);
								} else {
									updateCarrierPriceMargin({
										carrierId: carrierPrice.id,
										marginValue: event.target.value ?? 0,
									});
								}
							}}
							placeholder={"$0.00"}
							spinners={false}
							value={carrierPrice.brokerMargin ?? null}
						/>
					</div>
				),
		},
		{
			id: "service-level",
			group: "details",
			title: (
				<>
					Service Level
					{requiredIndicator}
				</>
			),
			className: "service-level-col",
			content: showInputs ? (
				<DropDownList
					data={
						Array.isArray(pageData?.serviceLevelData)
							? pageData.serviceLevelData
							: []
					}
					dataItemKey={"id"}
					defaultValue={null}
					disabled={isDisabled}
					onBlur={handleBlur}
					onChange={(e: { value: { id: number; name: string } | null }) => {
						updateAdHocCarrier(
							carrierPrice.id,
							"serviceLevel",
							e.value ?? { id: 0, name: "Select Service Level" },
						);
					}}
					textField={"name"}
					value={
						carrierPrice.serviceLevel?.id ? carrierPrice.serviceLevel : null
					}
				/>
			) : (
				carrierPriceDisplayName
			),
		},
		{
			id: "service-days",
			group: "details",
			title: (
				<>
					Transit Days
					{requiredIndicator}
				</>
			),
			className: "service-days-col",
			content: showInputs ? (
				<div className={"numeric-half"}>
					<NumericTextBox
						disabled={isDisabled}
						onBlur={handleBlur}
						onChange={(e) => {
							updateAdHocCarrier(carrierPrice.id, "transitDays", e.value ?? 0);
						}}
						placeholder={"0"}
						spinners={false}
						value={carrierPrice.transitDays ?? null}
					/>
					<span className={"days-est"}>days Est</span>
				</div>
			) : (
				<>
					{carrierPrice.transitDays}{" "}
					{carrierPrice.transitDays === 1 ? "Day" : "Days"} Est.
				</>
			),
		},
		{
			id: "service-type",
			group: "details",
			title: showInputs ? (
				<>
					Exp Date
					{requiredIndicator}
				</>
			) : (
				<>
					Service Type&nbsp;
					<Tooltip>
						<div title={"this is the tooltip"}>
							<SvgIcon icon={helpCircleLine} />
						</div>
					</Tooltip>
				</>
			),
			className: "service-type-col",
			content: showInputs ? (
				<div>
					<DatePicker
						disabled={isDisabled}
						onBlur={handleBlur}
						onChange={(e) => {
							updateAdHocCarrier(
								carrierPrice.id,
								"carrierQuoteExpirationDate",
								e.value ? e.value.toISOString() : "",
							);
						}}
						placeholder={"Select date"}
						value={
							carrierPrice.carrierQuoteExpirationDate
								? new Date(carrierPrice.carrierQuoteExpirationDate)
								: null
						}
					/>
				</div>
			) : (
				carrierPrice.serviceType
			),
		},
		{
			id: "carrier-quote",
			shouldShow: isInternal,
			group: "details",
			title: (
				<>
					Carrier Quote ID
					{requiredIndicator}
				</>
			),
			className: "carrier-quote-col",
			content: showInputs ? (
				<TextBox
					disabled={isDisabled}
					onBlur={handleBlur}
					onChange={(e) => {
						const newValue = (e.value as string) ?? "";
						setLocalCarrierQuoteId(newValue);
						updateAdHocCarrier(carrierPrice.id, "carrierQuoteId", newValue);
					}}
					value={localCarrierQuoteId}
				/>
			) : (
				carrierPrice.carrierQuoteId
			),
		},
		{
			id: "max-liability",
			group: "details",
			title: (
				<>
					Max Liability (New/Used)
					{showLiabilityWarning ? (
						<span
							className={"liability-warning-icon"}
							title={
								"Carrier\u2019s minimum liability does not cover the shipment value."
							}
						>
							<SvgIcon icon={shieldOff} />
						</span>
					) : null}
				</>
			),
			className: "max-liability-col",
			content: (
				<div>
					<div
						className={"max-liability-value"}
						data-testid={"max-liability-value"}
					>
						{carrierPrice.maxLiabilityOverride &&
						carrierPrice.maxLiabilityOverride > 0
							? formatCurrency(carrierPrice.maxLiabilityOverride)
							: `${formatCurrency(
									carrierPrice.maxLiabilityNew,
								)} / ${formatCurrency(carrierPrice.maxLiabilityUsed)}`}
					</div>
				</div>
			),
		},
	];
}
